import { Operation } from "rfc6902/diff";

/**
 * Interface for an action.
 **/
export default interface Action {
	/**
	 * Apply the action on the given state.
	 * @return The new state.
	 **/
	apply: <S>(state: S) => S;

	/**
	 * Revert the action on the given state.
	 * @return The new state.
	 **/
	revert: <S>(state: S) => S;

	/**
	 * Create a JSON patch style object for this action.
	 * @return The json patch list to represent this action.
	 **/
	patches: () => Operation[];
}

/**
 * An ActionGroup is a simple wrapper around multiple actions.
 * This allows you to perform these actions as if they were a single operation.
 * The actions are applied in given order and reverted in the oposite order.
 **/
export class ActionGroup implements Action {
	private readonly actions: Action[];

	constructor(actions: Action[]) {
		this.actions = actions;
	}

	public apply<S>(state: S): S {
		let newState = { ...state };
		for (const action of this.actions) {
			newState = action.apply(newState);
		}
		return newState;
	}

	public revert<S>(state: S): S {
		let newState = { ...state };
		const reversed = [...this.actions].reverse();
		for (const action of reversed) {
			newState = action.revert(newState);
		}
		return newState;
	}

	public patches(): Operation[] {
		return this.actions.flatMap((a) => a.patches());
	}
}
